/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

import api from "../../api/index.js";
import { Loader } from "../loader";
import { LoadingSpinner } from "../loading_spinner";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader/index.jsx";
import DropdownSearchBar from "../dorpdown_searchbar/index.jsx";
import { SelectedMenuItem } from "../selected_menu_item/index.jsx";
import { StyledMenuItem } from "../styled_menu_item/index.jsx";
import { useOtMutation } from "../../hooks/mutations/useOtMutation.js";
import { employeeMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/employeeMutationKeys.js";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver.js";
import { PerPage } from "../../constants/perPage.js";
import { getDropdownNextPageParam } from "../../utils/dropdownInfiniteQuery.js";
import { Query_Keys } from "../../lib/queryKeys.js";
import { useDebounce } from "../../utils/useDebounce.js";
import { validatePhoneNumber } from "../../utils/validatePhoneNumber.js";
import { validateZipCode, ZIP_CODE_ERROR_MESSAGE } from "../../utils/validateZipCode.js";
import { useNotificationContext } from "../../contexts/notificationContext";
import { uniqueJobs } from "../../pages/employee/utils/job_helpers";
import {
  StyledCredentialField,
  StyledIconButton,
} from "../../pages/restaurant/create/basic_information/styles.jsx";
import {
  StyledAddButton,
  StyledCrossButton,
  StyledDatePicker,
  StyledFormFieldText,
  StyledHelperText,
  StyledPhoneField,
  StyledSearchIconButton,
  StyledSelect,
  StyledTextField,
  StyledTypography,
} from "../../pages/network_guests/styles.jsx";
import {
  StyledCloseButton,
  StyledDialog,
  StyledFieldContainer,
  StyledModalActions,
  StyledModalContent,
  StyledModalHeader,
  StyledModalTitle,
  StyledPhoneRow,
} from "./styles.jsx";
import { ModalCancelButton } from "../modal_cancel_button/index.jsx"

const emptyEmployee = (restaurantId) => ({
  first_name: null,
  last_name: null,
  phone_number: "",
  email: null,
  zip_code: "",
  date_of_birth: null,
  jobs: [],
  restaurant_id: restaurantId || "",
});

export const EmployeeCreateModal = ({
  open,
  onClose,
  restaurantId,
  restaurantName,
  restaurantGroupName,
}) => {
  const showNotification = useNotificationContext();

  const [newEmployee, setNewEmployee] = useState(emptyEmployee(restaurantId));
  const [errors, setErrors] = useState({});
  const [jobTitleErrors, setJobTitleErrors] = useState([]);
  const [employeeFetched, setEmployeeFetched] = useState(false);
  const [employeeExist, setEmployeeExist] = useState(false);
  const [employeeFetchLoading, setEmployeeFetchLoading] = useState(false);
  const [associatedRestaurants, setAssociatedRestaurants] = useState([]);
  const [jobTitleSearch, setJobTitleSearch] = useState("");
  const [addJobTitle, setAddJobTitle] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newlyAddedJob, setNewlyAddedJob] = useState(null);

  const debouncedJobTitleSearch = useDebounce(jobTitleSearch.trim(), 300);

  const resetState = () => {
    setNewEmployee(emptyEmployee(restaurantId));
    setErrors({});
    setEmployeeFetched(false);
    setEmployeeExist(false);
    setEmployeeFetchLoading(false);
    setAssociatedRestaurants([]);
    setAddJobTitle(false);
    setNewJobTitle("");
    setNewlyAddedJob(null);
    setJobTitleSearch("");
    setJobTitleErrors([]);
  };

  // Re-seed the form whenever the modal is (re)opened or the restaurant changes.
  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, restaurantId]);

  const addJobMutation = useOtMutation({
    mutationFn: (variables) => api.post("/v1/admin/jobs", variables),
    invalidateKeys: [["jobs"], ["jobs_dropdown"]],
    successMessage: "New job added successfully",
  });

  const addEmployeeMutation = useOtMutation({
    mutationFn: (variables) => api.post("/v1/admin/employees", variables),
    invalidateKeys: employeeMutationKeyGroups.create,
    successMessage: "Employee has been added successfully",
  });

  const loading = addJobMutation.isPending || addEmployeeMutation.isPending;

  const fetchJobsList = async ({ pageParam = 1 }) => {
    const res = await api.get("/v1/admin/dropdowns/jobs_list", {
      params: {
        restaurant_ids: restaurantId,
        page: pageParam,
        per_page: PerPage,
        search: debouncedJobTitleSearch || undefined,
      },
    });
    return res?.data || [];
  };

  const {
    data: jobsList = [],
    isFetchingNextPage: jobsListFetchingNextPage,
    fetchNextPage: fetchNextJobsListPage,
    hasNextPage: hasNextJobsListPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.jobs_dropdown({
      new_emp_restaurant_id: restaurantId,
      search: debouncedJobTitleSearch,
      source: "employee_create_modal",
    }),
    queryFn: fetchJobsList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    enabled: open && Boolean(restaurantId),
    placeholderData: keepPreviousData,
  });

  const { rootRef: jobsListMenuRef, targetRef: jobsListLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextJobsListPage,
    hasNextPage: hasNextJobsListPage,
    isFetchingNextPage: jobsListFetchingNextPage,
  });

  const filteredJobTitles = uniqueJobs(jobsList);

  const handleInputChange = (value, field) => {
    if (field === "restaurant_id") {
      setNewEmployee({ ...newEmployee, [field]: value, jobs: [] });
    } else {
      setNewEmployee({ ...newEmployee, [field]: value });
    }
  };

  const handleDateOfBirth = (event) => {
    setNewEmployee({ ...newEmployee, date_of_birth: dayjs(event) });
    if (errors["date_of_birth"] !== "") {
      setErrors({ ...errors, ["date_of_birth"]: "" });
    }
  };

  const searchEmployee = async () => {
    if (!validatePhoneNumber(newEmployee?.phone_number)) {
      setErrors((prev) => ({ ...prev, phone_number: "Phone number is required" }));
      return;
    }

    // Clear any stale form errors from a previous attempt before re-searching.
    setErrors({});
    setEmployeeFetchLoading(true);
    try {
      const res = await api.get("/v1/admin/network_guests/find_guest_by_number", {
        params: { phone_number: newEmployee?.phone_number },
      });
      setNewEmployee({
        ...newEmployee,
        first_name: res?.data?.[0]?.first_name,
        last_name: res?.data?.[0]?.last_name,
        email: res?.data?.[0]?.email,
        zip_code: res?.data?.[0]?.zip_code,
        date_of_birth: res?.data?.[0]?.date_of_birth,
      });
      setAssociatedRestaurants(res?.data?.[0]?.employee_restaurants);
      setEmployeeExist(true);
      setEmployeeFetched(true);
    } catch (error) {
      // Only a genuine 404 means "no guest for this phone" and is safe to proceed
      // with creation. Transport/server errors (timeout, 5xx, 403, …) must NOT fall
      // through to the create form, otherwise the user could create a duplicate
      // guest for a phone number that actually already exists.
      if (error?.response?.status === 404) {
        setEmployeeExist(false);
        setEmployeeFetched(true);
      } else {
        setEmployeeExist(false);
        setEmployeeFetched(false);
        showNotification(
          "Unable to verify this phone number right now. Please try again.",
          "error"
        );
      }
    } finally {
      setEmployeeFetchLoading(false);
    }
  };

  const handleClearEmployee = () => {
    setEmployeeExist(false);
    setEmployeeFetched(false);
    setNewEmployee(emptyEmployee(restaurantId));
    setAssociatedRestaurants([]);
  };

  const handleJobTitleChange = (event) => {
    if (event.target.value.includes("add_new")) {
      setJobTitleErrors([]);
      setAddJobTitle(true);
    } else {
      const selectedJobIds = event.target.value;

      const selectedJobs = selectedJobIds.map((jobId) => {
        const existingJob = newEmployee.jobs.find((j) => j.id === jobId);
        if (existingJob) {
          return existingJob;
        }

        const jobFromList = jobsList?.find((job) => job.id === jobId);
        if (jobFromList) {
          return {
            id: jobId,
            title: jobFromList.title,
            wage: jobFromList.default_wage,
          };
        }
      });

      setNewEmployee({ ...newEmployee, jobs: selectedJobs });

      if (selectedJobs?.length > 0) {
        setErrors((prev) => ({ ...prev, base: null }));
      }
    }
  };

  const handleJobTitleClear = () => {
    setNewEmployee({ ...newEmployee, jobs: [] });
    setNewlyAddedJob(null);
  };

  const handleWageChange = (jobId, value) => {
    setNewEmployee((prev) => ({
      ...prev,
      jobs: prev?.jobs?.map((job) =>
        job?.id === jobId ? { ...job, wage: value } : job
      ),
    }));
  };

  const handleAddOption = () => {
    addJobMutation.mutate(
      {
        restaurant_id: restaurantId,
        job: {
          title: newJobTitle,
        },
      },
      {
        onSuccess: (res) => {
          const newJob = {
            id: res?.data?.id,
            title: res?.data?.title,
            wage: res?.data?.default_wage,
            source_of_truth: "manual",
          };
          setAddJobTitle(false);
          setNewJobTitle("");
          setJobTitleErrors([]);
          setNewEmployee((prev) => ({
            ...prev,
            jobs: [...prev.jobs, newJob],
          }));
          setNewlyAddedJob(newJob.id);
          setErrors((prev) => ({ ...prev, base: null }));
        },
        onError: (error) => {
          setJobTitleErrors(error?.response?.data?.errors?.title || []);
        },
      }
    );
  };

  const handleAddEmployee = () => {
    if (!restaurantId) {
      setErrors((prev) => ({ ...prev, restaurant_id: "Please select a restaurant" }));
      showNotification("Please select a restaurant", "error");
      return;
    }

    if (!validateZipCode(newEmployee?.zip_code)) {
      setErrors((prev) => ({ ...prev, zip_code: ZIP_CODE_ERROR_MESSAGE }));
      showNotification(ZIP_CODE_ERROR_MESSAGE, "error");
      return;
    }

    // Normalize blank required text fields to "" so the param is always present.
    // Otherwise an untouched field is sent as undefined (param missing → "is required")
    // while a filled-then-cleared field is sent as "" ("cannot be blank"), which is
    // inconsistent. Always sending "" yields a single, consistent validation message.
    let data = {
      ...newEmployee,
      restaurant_id: restaurantId,
      first_name: newEmployee?.first_name ?? "",
      last_name: newEmployee?.last_name ?? "",
    };
    if (data?.date_of_birth) {
      const currentDate = new Date(data?.date_of_birth);
      const utcDate = new Date(
        currentDate.getTime() - currentDate.getTimezoneOffset() * 60 * 1000
      );
      data.date_of_birth = utcDate;
    }

    if (newEmployee?.phone_number?.length <= 4) {
      delete data.phone_number;
    }

    addEmployeeMutation.mutate(
      {
        restaurant_id: restaurantId,
        employee: data,
      },
      {
        onSuccess: () => {
          resetState();
          onClose();
        },
        onError: (error) => {
          setErrors(error?.response?.data?.errors);
          if (error?.response?.data?.errors?.already_exists) {
            showNotification(error?.response?.data?.errors?.already_exists, "error");
          }
        },
      }
    );
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} data-testid="employee-create-modal">
      <StyledModalHeader>
        <StyledModalTitle>Add Employee</StyledModalTitle>
        <StyledCloseButton onClick={handleClose} size="small">
          <CloseIcon />
        </StyledCloseButton>
      </StyledModalHeader>

      <StyledModalContent>
        <div>
          <StyledFormFieldText>
            Phone number<span style={{ color: "red" }}>*</span>
          </StyledFormFieldText>
          <StyledPhoneRow>
            <div style={{ width: "100%" }}>
              <StyledPhoneField
                value={newEmployee?.phone_number}
                onChange={(e) => {
                  handleInputChange(e, "phone_number");
                  setErrors((prev) => ({ ...prev, phone_number: null }));
                }}
                defaultCountry="us"
                forceDialCode
                borderColor={errors?.phone_number ? "red" : "#c4c4c4"}
                data-testid="create-phone"
                disabled={employeeFetched}
              />
            </div>
            {employeeFetched ? (
              <StyledCrossButton>
                <ClearIcon onClick={handleClearEmployee} />
              </StyledCrossButton>
            ) : (
              <StyledSearchIconButton
                aria-label="Search Employee by Phone"
                onClick={searchEmployee}
                disabled={employeeFetchLoading}
                disableRipple
              >
                <SearchIcon />
              </StyledSearchIconButton>
            )}
          </StyledPhoneRow>
          <StyledHelperText
            style={{ display: errors?.phone_number ? "inline" : "none" }}
          >
            {errors?.phone_number && `${errors?.phone_number}`}
          </StyledHelperText>
        </div>

        {employeeFetchLoading && <Loader />}

        {employeeFetched && (
          <>
            <StyledFieldContainer>
              {employeeExist ? (
                <Alert variant="outlined" severity="error">
                  This phone belongs to an employee record. Adding an employee with the
                  same phone number would associate new record with the already existing
                  one.
                </Alert>
              ) : (
                <Alert variant="outlined" severity="info">
                  This phone does not belong to any employee record. Please proceed by
                  filling the required details.
                </Alert>
              )}
            </StyledFieldContainer>

            {associatedRestaurants?.length > 0 && (
              <StyledFieldContainer>
                <StyledFormFieldText>Active Restaurants: </StyledFormFieldText>
                <StyledTypography variant="caption" color="text.secondary" display="block">
                  {associatedRestaurants.map((res) => res?.name).join(", ")}
                </StyledTypography>
              </StyledFieldContainer>
            )}

            <StyledFieldContainer>
              <StyledFormFieldText>Restaurant group</StyledFormFieldText>
              <StyledSelect
                value={restaurantGroupName || ""}
                disabled
                displayEmpty
                renderValue={() => restaurantGroupName || "Select"}
              >
                <StyledMenuItem value={restaurantGroupName || ""}>
                  {restaurantGroupName || "Select"}
                </StyledMenuItem>
              </StyledSelect>
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                Restaurant<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledSelect
                value={restaurantId || ""}
                disabled
                displayEmpty
                renderValue={() => restaurantName || "Select"}
              >
                <StyledMenuItem value={restaurantId || ""}>
                  {restaurantName || "Select"}
                </StyledMenuItem>
              </StyledSelect>
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                First name<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "first_name");
                  setErrors((prev) => ({ ...prev, first_name: null }));
                }}
                error={errors?.first_name}
                helperText={errors?.first_name}
                value={newEmployee?.first_name || ""}
                data-testid="create-first-name"
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                Last name<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "last_name");
                  setErrors((prev) => ({ ...prev, last_name: null }));
                }}
                error={errors?.last_name}
                helperText={errors?.last_name}
                value={newEmployee?.last_name || ""}
                data-testid="create-last-name"
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>Email address</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "email");
                  setErrors((prev) => ({ ...prev, email: null }));
                }}
                error={errors?.email?.[0]}
                helperText={errors?.email?.[0]}
                value={newEmployee?.email || ""}
                data-testid="create-email"
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>Zip code</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                inputProps={{ "data-testid": "create-zip-code-input" }}
                onChange={(e) => {
                  handleInputChange(e.target.value, "zip_code");
                  setErrors((prev) => ({ ...prev, zip_code: null }));
                }}
                error={Boolean(errors?.zip_code)}
                helperText={
                  Array.isArray(errors?.zip_code) ? errors?.zip_code?.[0] : errors?.zip_code
                }
                value={newEmployee?.zip_code || ""}
                data-testid="create-zip-code"
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>Date of Birth</StyledFormFieldText>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  value={dayjs(newEmployee?.date_of_birth)}
                  onChange={(event) => handleDateOfBirth(event)}
                  slotProps={{
                    textField: {
                      placeholder: "Date of Birth",
                      error: !!errors?.date_of_birth,
                      helperText: errors?.date_of_birth?.[0]?.includes("must be less than")
                        ? "Cannot be a future or present date"
                        : errors?.date_of_birth,
                    },
                  }}
                />
              </LocalizationProvider>
            </StyledFieldContainer>

            {restaurantId && (
              <StyledFieldContainer>
                <StyledFormFieldText>
                  Jobs<span style={{ color: "red" }}>*</span>
                </StyledFormFieldText>

                <Box>
                  {addJobTitle ? (
                    <div>
                      <StyledCredentialField
                        style={{ width: "100%", height: "2.6rem" }}
                        variant="outlined"
                        placeholder="Enter the title of job"
                        fullWidth
                        value={newJobTitle}
                        onChange={(e) => setNewJobTitle(e.target.value)}
                        error={jobTitleErrors?.length > 0}
                        endAdornment={
                          <InputAdornment position="end">
                            <StyledIconButton onClick={() => handleAddOption()} disabled={loading}>
                              <CheckCircleIcon color={loading ? "disabled" : "success"} />
                            </StyledIconButton>
                            <StyledIconButton onClick={() => setAddJobTitle(false)}>
                              <CancelIcon sx={{ color: "red" }} />
                            </StyledIconButton>
                          </InputAdornment>
                        }
                        autoFocus
                      />
                      {jobTitleErrors?.length > 0 && (
                        <FormHelperText error>{jobTitleErrors[0]}</FormHelperText>
                      )}
                    </div>
                  ) : (
                    <>
                      <StyledSelect
                        data-testid="create-jobs-select"
                        value={newEmployee?.jobs.map((job) => job.id)}
                        onChange={handleJobTitleChange}
                        onClose={() => setTimeout(() => setJobTitleSearch(""), 1000)}
                        error={!!errors?.base}
                        MenuProps={{
                          PaperProps: {
                            ref: jobsListMenuRef,
                            sx: { maxHeight: 400 },
                          },
                        }}
                        multiple
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return "";
                          }
                          if (selected.length === 1) {
                            const job = newEmployee.jobs.find((j) => j.id === selected[0]);
                            return job?.title || "";
                          }
                          return `${selected.length} Jobs Selected`;
                        }}
                        endAdornment={
                          newEmployee?.jobs?.length > 0 ? (
                            <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                              <IconButton
                                edge="end"
                                size="small"
                                disableRipple
                                onClick={handleJobTitleClear}
                              >
                                <ClearIcon sx={{ fontSize: "20px" }} />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }
                      >
                        <DropdownSearchBar
                          changeSearchText={setJobTitleSearch}
                          placeholder={"Search job title"}
                        />

                        {filteredJobTitles?.map((job) => (
                          <StyledMenuItem key={job?.id} value={job?.id}>
                            {newEmployee?.jobs?.some((j) => j.id === job.id) ? (
                              <SelectedMenuItem data={job?.title} />
                            ) : (
                              <>{`${job?.title}`}</>
                            )}
                          </StyledMenuItem>
                        ))}
                        <DropdownInfiniteLoader
                          hasNextPage={hasNextJobsListPage}
                          isFetching={jobsListFetchingNextPage}
                          targetRef={jobsListLoaderRef}
                        />

                        <StyledMenuItem value={"add_new"}>
                          <AddIcon sx={{ fontSize: 18, marginRight: 1 }} />
                          Add New Job
                        </StyledMenuItem>
                      </StyledSelect>

                      {errors?.base && (
                        <FormHelperText error sx={{ ml: 2, mt: 1 }}>
                          {errors.base}
                        </FormHelperText>
                      )}

                      {/* Display wage fields for each selected job */}
                      {newEmployee.jobs.map((job) => (
                        <Box
                          key={job.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mt: 2,
                            p: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            backgroundColor: newlyAddedJob === job.id ? "#f0f8ff" : "transparent",
                          }}
                        >
                          <Box sx={{ minWidth: "150px" }}>
                            <StyledFormFieldText>Wage({job.title})</StyledFormFieldText>
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <StyledTextField
                              size="small"
                              placeholder="Enter wage"
                              value={job.wage || ""}
                              onChange={(e) => handleWageChange(job.id, e.target.value)}
                              error={!!errors?.wage}
                              helperText={errors?.wage}
                              autoFocus={newlyAddedJob === job.id}
                              onFocus={() => setNewlyAddedJob(null)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              </StyledFieldContainer>
            )}
          </>
        )}
      </StyledModalContent>

      <StyledModalActions>
        <ModalCancelButton onClick={handleClose}>
          Cancel
        </ModalCancelButton>
        <StyledAddButton
          onClick={handleAddEmployee}
          variant="contained"
          disabled={loading || !employeeFetched}
        >
          {loading ? <LoadingSpinner isLoading={true} /> : "Add"}
        </StyledAddButton>
      </StyledModalActions>
    </StyledDialog>
  );
};

export default EmployeeCreateModal;
